import React, { Dispatch, SetStateAction } from 'react';
import { retryScript } from '../../../api';
import { Episode, Project, ScriptAnalysis } from '../../../types';
import ProjectInfoSummary from './ProjectInfoSummary';
import ProjectScriptPanel from './ProjectScriptPanel';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ProjectDetailHeaderSectionProps {
  project: Project;
  projectId?: string;
  episodes: Episode[];
  analysis: ScriptAnalysis | null;
  polling: boolean;
  selectedFile: File | null;
  uploading: boolean;
  appendFile: File | null;
  appending: boolean;
  showAppendForm: boolean;
  scriptMenuOpen: boolean;
  scriptMenuRef: React.RefObject<HTMLDivElement>;
  t: Translate;
  toast: {
    warning: (message: string) => void;
  };
  setProject: Dispatch<SetStateAction<Project | null>>;
  setPolling: Dispatch<SetStateAction<boolean>>;
  setAppendFile: Dispatch<SetStateAction<File | null>>;
  setShowAppendForm: Dispatch<SetStateAction<boolean>>;
  setShowFormatGuide: Dispatch<SetStateAction<boolean>>;
  setScriptMenuOpen: (open: boolean | ((previous: boolean) => boolean)) => void;
  handleAppend: () => void;
  handleUpload: (event: React.FormEvent) => void;
  loadProject: (projectId: string) => void;
  loadEpisodes: (projectId: string) => void;
  loadAnalysis: (projectId: string) => Promise<void | boolean>;
}

export default function ProjectDetailHeaderSection({
  project,
  projectId,
  episodes,
  analysis,
  polling,
  selectedFile,
  uploading,
  appendFile,
  appending,
  showAppendForm,
  scriptMenuOpen,
  scriptMenuRef,
  t,
  toast,
  setProject,
  setPolling,
  setAppendFile,
  setShowAppendForm,
  setShowFormatGuide,
  setScriptMenuOpen,
  handleAppend,
  handleUpload,
  loadProject,
  loadEpisodes,
  loadAnalysis,
}: ProjectDetailHeaderSectionProps) {
  return (
    <div id="step-script" className="project-detail-top" style={{ display: 'flex', gap: '0.75rem', alignItems: 'stretch', marginBottom: '1rem', flexWrap: 'wrap' }}>
      <div className="card project-info-panel theme-left-border" style={{ flex: '1 1 auto', width: '100%', borderRadius: '10px', padding: '1rem', margin: 0, display: 'flex', gap: '1rem', '--_theme-color': 'var(--theme-project)', '--_theme-color-end': '#a78bfa' } as React.CSSProperties}>
        <ProjectInfoSummary
          project={project}
          t={t}
        />

        <div className="project-info-divider" style={{ width: '1px', background: 'var(--border)', alignSelf: 'stretch', flexShrink: 0 }} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <ProjectScriptPanel
            project={project}
            episodes={episodes}
            analysisStatus={analysis?.status}
            polling={polling}
            selectedFile={selectedFile}
            uploading={uploading}
            appendFile={appendFile}
            appending={appending}
            showAppendForm={showAppendForm}
            scriptMenuOpen={scriptMenuOpen}
            scriptMenuRef={scriptMenuRef}
            t={t}
            onAppendFileChange={setAppendFile}
            onShowAppendForm={() => setShowAppendForm(true)}
            onCancelAppend={() => { setShowAppendForm(false); setAppendFile(null); }}
            onAppend={handleAppend}
            onUpload={handleUpload}
            onOpenFormatGuide={() => setShowFormatGuide(true)}
            onRetryScript={async () => {
              if (!projectId) return;
              try {
                await retryScript(projectId);
                setPolling(true);
                loadProject(projectId);
              } catch (err) {
                console.error('Failed to retry script:', err);
              }
            }}
            onReupload={() => setProject({ ...project, script: undefined })}
            onScriptMenuOpenChange={setScriptMenuOpen}
            onImported={() => { if (projectId) { loadProject(projectId); loadEpisodes(projectId); loadAnalysis(projectId); } }}
            onInvalidFile={(messageKey = 'script.fileFormatOnly') => toast.warning(t(messageKey))}
          />
        </div>
      </div>
    </div>
  );
}
